import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ToastService } from '../../../core/services/toast.service';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { take } from 'rxjs';
import { EmissionService } from '../../../core/services/emission.service';
import { ResultatAdjudicationDTO } from '../../../core/models/emission.model';

@Component({
    selector: 'app-resultat-details',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ButtonModule,
        InputTextModule,
        InputNumberModule
    ],
    templateUrl: './resultat-details.component.html',
    styleUrl: './resultat-details.component.scss'
})
export class ResultatDetailsComponent implements OnInit {
  private toastService = inject(ToastService);
  private ref = inject(DynamicDialogRef);
  private config = inject(DynamicDialogConfig);
  private emissionService = inject(EmissionService);

  formGroup = new FormGroup({
    montantSollicite: new FormControl<number>(0, { validators: [Validators.required, Validators.min(0)] }),
    montantGlobalSoumissions: new FormControl<number>(0, { validators: [Validators.required, Validators.min(0)] }),
    tauxMoyenPondere: new FormControl<number>(0, { validators: [Validators.required, Validators.min(0)] }),
    tauxMarginal: new FormControl<number>(0, { validators: [Validators.required, Validators.min(0)] }),
    nombreSoumissions: new FormControl<number>(0, { validators: [Validators.required, Validators.min(0)] }),
    soumissionsRetenues: new FormControl<number>(0, { validators: [Validators.required, Validators.min(0)] }),
    rendementMoyenPondere: new FormControl<number>(0, { validators: [Validators.required, Validators.min(0)] })
  });

  titre: any;
  isView = false;
  resultatId?: string;
  isEditMode = false;

  ngOnInit() {
    if (this.config.data?.data) {
      this.titre = this.config.data.data;
      this.isView = this.config.data.isView || false;

      // Récupérer les dates de l'émission associée
      if (this.titre.emission) {
        this.titre.operationDate = this.titre.emission.operationDate;
        this.titre.valueDate = this.titre.emission.valueDate;
      }

      console.log('Titre pour résultat:', this.titre);

      // Charger le résultat existant s'il existe
      this.loadResultat();
    }
  }

  loadResultat() {
    this.emissionService.getResultatByTitreId(this.titre.id)
      .pipe(take(1))
      .subscribe({
        next: (resultat) => {
          this.resultatId = resultat.id;
          this.formGroup.patchValue({
            montantSollicite: resultat.montantSollicite,
            montantGlobalSoumissions: resultat.montantGlobalSoumissions,
            tauxMoyenPondere: resultat.tauxMoyenPondere,
            tauxMarginal: resultat.tauxMarginal,
            nombreSoumissions: resultat.nombreSoumissions,
            soumissionsRetenues: resultat.soumissionsRetenues,
            rendementMoyenPondere: resultat.rendementMoyenPondere
          });

          // Si on est en mode consultation, désactiver le formulaire
          if (this.isView) {
            this.formGroup.disable();
          }
        },
        error: (error) => {
          // Pas de résultat existant, c'est normal si on crée un nouveau
          if (error.status !== 404) {
            console.error('Erreur lors du chargement du résultat:', error);
          }
        }
      });
  }

  save() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      this.toastService.showToast('Attention', 'Veuillez remplir tous les champs obligatoires', 'warn');
      return;
    }

    const formValue = this.formGroup.getRawValue();

    const resultatDTO: ResultatAdjudicationDTO = {
      montantSollicite: formValue.montantSollicite!,
      montantGlobalSoumissions: formValue.montantGlobalSoumissions!,
      tauxMoyenPondere: formValue.tauxMoyenPondere!,
      tauxMarginal: formValue.tauxMarginal!,
      nombreSoumissions: formValue.nombreSoumissions!,
      soumissionsRetenues: formValue.soumissionsRetenues!,
      rendementMoyenPondere: formValue.rendementMoyenPondere!,
      titreId: this.titre.id
    };

    // Si on a un resultatId, on fait une mise à jour, sinon on crée
    const observable = this.resultatId
      ? this.emissionService.updateResultatAdjudication(this.resultatId, resultatDTO)
      : this.emissionService.createResultatAdjudication(resultatDTO);

    observable.pipe(take(1)).subscribe({
      next: () => {
        const message = this.resultatId ? 'Résultat modifié avec succès' : 'Résultat ajouté avec succès';
        this.toastService.showToast('Succès', message, 'success');
        this.ref.close(true);
      },
      error: (error) => {
        console.error('Erreur lors de la sauvegarde du résultat:', error);
        this.toastService.showToast('Erreur', 'Erreur lors de la sauvegarde du résultat', 'error');
      }
    });
  }

  enableEdit() {
    this.isEditMode = true;
    this.formGroup.enable();
  }

  cancel() {
    this.ref.close();
  }
}
